// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import { IDeviceConfigKey } from './key';
import { BitlockerPolicySection, IPolicyPbaSettings, IPolicyFdeSettings } from './subscription';

export interface ID3EPolicy {
    disabledExts?: string[];
    protectedExts: string[];
    lockedExts: string[];
    ransomExts?: string[];
    disabledDirs?: string[];
    protectedDirs: string[];
    lockedDirs: string[];
    encryptedProtectedDirs: IEncryptedDirectory[];
    encryptedLockedDirs: IEncryptedDirectory[];
    deceptionFiles: string[];
    protectedVolumes?: string[];
    sensors?: ISensor[];
    knownGood?: IKnownGoodApp[];
    // overrideLocalPolicy?: boolean;
    syncInterval?: number;
    syncIntervalMetered?: number;
    licenseInterval?: number;
    licenseIntervalMetered?: number;
    forcePreAuthValues?: validReAuthFrequencyValues;
    preAuthFileAccessCount?: number;
    preAuthFileAccessTimeout?: validTimeoutValues;
    ransomwareSensitivityMin?: number;
    ransomwareSensitivityMax?: number;
    ransomwareFeatureMask?: ransomwareFeatureMaskValues;
    bitlocker?: BitlockerPolicySection;
    pbaSettings?: IPolicyPbaSettings;
    fdeSettings?: IPolicyFdeSettings;
}

export type validTimeoutValues = 0 | 10 | 30 | 60 | 240 | 480;
export type ransomwareFeatureMaskValues = 0 | 1 | 3 ;
export type validReAuthFrequencyValues = 0 | 1;

export interface ID3EPolicyRequest {
    name: string;
    policy: ID3EPolicy;
    overrideLocalPolicy: boolean;
    enabled?: boolean;
    allowAlwaysOn: boolean;
}

export interface ISensor {
    sensorType: number;
    enabled: boolean;
    allowDeactivate?: boolean;
}

export interface IDeviceSet {
    id: number;
    name: string;
}

export interface IHomeConfig {
    check_time_throttle: number;
    configuredIntegrationPartnerIds: number[];
    d3e_detail_page: boolean;
    deviceSets: IDeviceSet[];
    device_page_refresh: number;
    disconnect_threshold: number;
    group_lock_enabled: boolean;
    sensor_types: ISensorType[];
    show_force_sync_btn: boolean;
}

export interface ISensorType {
    value: number;
    displayValue: string;
    description: string;
    hidden: boolean;
}

export interface IForcePolicyResetRequest {
    licenses: string[];
}

export interface ID3EPolicyResponse {
    id: number;
    name: string;
    policy: ID3EPolicy;
    overrideLocalPolicy: boolean;
    enabled?: boolean;
    show_force_policy_checkbox: boolean;
    show_environment_variables: boolean;
    allowAlwaysOn: boolean;
    sub_key_uuids: string[];
    pba_users: IPbaUser[];
}

export interface IKnownGoodApp {
    displayName: string;
    app?: number;
    enabled: boolean;
    subject: IX509Name;
    strength: number;
    paths: string[];
}

export interface IX509Name {
    CN: string;
    C?: string;
    L?: string;
    O?: string;
    ST?: string;
    OU?: string;
    emailAddress?: string;
}

export interface ICertificateSubjectResponse {
    strength: string;
    subject: IX509Name;
}

export const DISCRETE_KEYS: string[] = [
    'protectedDirs',
    'lockedDirs',
    'protectedExts',
    'lockedExts',
    'deceptionFiles',
];

export const ENCRYPTED_DISCRETE_KEYS: string[] = [
    'encryptedProtectedDirs',
    'encryptedLockedDirs'
];

export type ProtectionLevel =
    | 'none'
    | 'medium'
    | 'high'
    | 'medium-encrypted'
    | 'high-encrypted';

export type EncryptionLevel =
    | 'none-encrypted'
    | 'encrypted';

export interface IEffectivePolicyResponse {
    policy_id: number;
    policy_name: string;
    last_policy_dttm: string;
    priority: number;
    override_local_policy: number;
    policy: ID3EPolicy;
    effective_user_id: string;
    effective_group_id: string;
}

export interface IPolicyControlCache {
    originalValue: string;
    newValue: string;
    editMode: boolean;
    setting: ProtectionLevel;
    encryptedSetting?: EncryptionLevel;
}

export interface IDeceptionControlCache {
    originalValue: string;
    newValue: string;
}

export interface IPolicyFeatureFlags {
    knownGoodTab: boolean;
    deceptionTab: boolean;
    bitlockerTab: boolean;
    pbaTab: boolean;
    fdeTab: boolean;
}

export interface IClientPolicyDefaults {
    prohibitedLockedDirs?: string[];
    prohibitedLockedExts?: string[];
    prohibitedProtectedDirs?: string[];
    prohibitedProtectedExts?: string[];
    sections: IPolicySection[];
    excludedDirs?: string[];
}

export interface IPolicySection {
    active?: boolean;
    groups?: IPolicyGroup[];
    icon?: string;
    name?: string;
    defaults?: IKnownGoodDefaults[];
    recommended?: IKnownGoodDefaults[];
}

export interface IPolicyGroup {
    active?: boolean;
    extensions?: IPolicyExtension[];
    folders?: IPolicyFolder[];
    settings?: IPolicySetting[];
    icon?: string;
    name?: string;
}

export interface IKnownGoodDefaults {
   app: number;
   subject: string;
   strength: number;
   displayName?: string;
   enabled?: boolean;
   paths?: string[];
}

export interface IPolicyExtension {
    active?: boolean;
    description?: string;
    details?: string;
    extension: string;
    value: ProtectionLevel;
    policyValue?: ProtectionLevel;
}

export interface IPolicyFolder {
    active?: boolean;
    folder: string;
    value: string;
    policyValue?: ProtectionLevel;
    encryptValue?: EncryptionLevel;
}

export interface IPolicySetting {
    active?: boolean;
    name: string;
    value: number;
    policyValue: number;
    min: number;
    max: number;
}

export interface IPolicyExtensions {
    disabledExtensions?: string[];
    protectedExtensions: string[];
    lockedExtensions: string[];
}

export interface IRansomExtensions {
    ransomExtensions: string[];
}


export interface IPolicyFolders {
    disabledFolders?: string[];
    protectedFolders: string[];
    lockedFolders: string[];
    encryptedProtectedFolders: IEncryptedDirectory[];
    encryptedLockedFolders: IEncryptedDirectory[];
}

export interface IEncryptedDirectory {
    dir: string;
    key_uuids: string[];
    subscription_keys: boolean;
}

export interface IDeviceConfigEncryptedDirectory extends IEncryptedDirectory {
    // the following attribute is only populated by a call to /home/device/<device_id>
    key_data?: IDeviceConfigKey[]; // encryptionScope will be determined by a valid key from this list
    keyIndex?: number;  // index into key_data
}

export interface IPolicyListItem {
    id: number;
    name: string;
    priority: number;
    override_local_policy: boolean;
}

export interface IPolicyImport {
    value: string;
    setting: string;
    encryptedSetting?: string;
}

export interface IPolicyCreatedResponse {
    new_id: number;
}

export interface IEndPointPolicy {
    id: number;
    name: string;
}

export interface IEndPointsConfigResponse {
    deviceSetsEnabled: boolean;
    deviceSets: IDeviceSet[];
    policies: IEndPointPolicy[];
}

export interface IPbaUser {
    id?: number;
    disk_serial: string;
    username: string;
    email?: string;
    role?: ProductUserRole;
    password?: string;
    action?: ConsoleAction;
}

export interface IPbaEnrollment {
    id?: number;
    disk_serial: string;
    enroll_code_id?: string;
    expires_dttm?: string;
    usages_remaining?: number;
    action?: ConsoleAction;
}

export interface IFdeUser {
    id?: number;
    disk_serial: string;
    username: string;
    email?: string;
    role?: ProductUserRole;
    password?: string;
    action?: ConsoleAction;
}

export interface IFdeEnrollment {
    id?: number;
    disk_serial: string;
    enroll_code_id?: string;
    expires_dttm?: string;
    usages_remaining?: number;
    action?: ConsoleAction;
}

export interface ISelfEnrollment {
    enroll_code_id: string;
    usages_remaining: number;
    expires: number;
    console_enroll_code_id?: number;
}

export interface IPbaLicenseInfo {
    serial: string;
    license_id: number;
    created_dttm: string;
    maintenance_end_dttm: string;
    license_exp_dttm: string;
}

export interface IFdeLicenseInfo {
    serial: string;
    license_id: number;
    created_dttm: string;
    maintenance_end_dttm: string;
    license_exp_dttm: string;
}

export enum PolicyCompliance {
    UNKNOWN = 1,
    COMPLIANT = 2,
    NON_COMPLIANT = 3,
    NOT_APPLICABLE = 4,
}

export enum ProductUserRole {
    Invalid = 0,
    User = 1,
    UserAdmin = 2,
    Admin = 3,
}

export enum ConsoleAction {
    DEACTIVATE_PBA = 0,
    REACTIVATE_PBA = 1,
    LOCK_PBA_RANGES = 2,
    UNLOCK_PBA_RANGES = 3,
    ERASE_PBA = 4,
    SET_PBA_ERASE_AFTER = 5,
    CLEAR_PBA_ERASE_AFTER = 6,
    REMOVE_ENROLLCODES_PBA = 7,
    DEACTIVATE_FDE = 8,
    REACTIVATE_FDE = 9,
    ERASE_FDE = 10,
    REMOVE_ENROLLCODES_FDE = 11,
    REMOVE_PBA_USER = 12,
    REMOVE_FDE_USER = 13,
    ADD_PBA_USER = 14,
    EDIT_PBA_USER = 15,
    ADD_FDE_USER = 16,
    EDIT_FDE_USER = 17,
    ADD_ENROLLCODES_PBA = 18,
    ADD_ENROLLCODES_FDE = 19,
    CANCEL_PBA_USER_ACTION = 20,
    CANCEL_FDE_USER_ACTION = 21,
    CLEAR_ALL_PBA_ACTIONS = 22,
    CLEAR_ALL_FDE_ACTIONS = 23,
}

export interface IFeatureMaskOption {
    'value': ransomwareFeatureMaskValues;
    'label': string;
}

export interface IReAuthFrequency {
    'value': validReAuthFrequencyValues;
    'label': string;
}

export interface IEndpointActionData {
    license_id: number;
    pba_pw_min?: number;
    fde_pw_min?: number;
    pba_users?: IPbaUser[];
    fde_users?: IFdeUser[];
    pending_pba_users?: IPbaUser[];
    pending_fde_users?: IFdeUser[];
}